// Logic
import getCacheKey from "@/vue/planning/logic/getCacheKey"
import { getUniqueId } from "@/vue/planning/logic/uniqueId"

// State
import responsesCache from "@/vue/planning/state/responsesCache"
import pricesLoadingSet from "@/vue/planning/state/pricesLoadingSet"

// Typees
import {
	IResaPrestation,
	PricesResponse,
	WiypResponse,
	WiypPrestation,
	PrestationRaw,
} from "@/types/responses"
import { ISeAvailabilities, PackageOffer } from "@/types"
import { IStoreState } from "@/vue/planning/store/state/state"

// Various
import * as _ from "lodash"
import transportPackCode from "@/config/transportPackCode"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import useLocale from "@/vue/composables/useLocale"
import { getCookie } from "@/utils/utils"

const locale = useLocale()

import { airportCodeMap } from "@/config/airportCodeMap";
import { trainStationCodeMap } from "@/config/trainStationCodeMap";

const getOrigin = (
	storeObj: { state: IStoreState; [key: string]: any },
	packCode: string
): string | undefined => {
	if (!packCode) return undefined;

	const rawOrigin = (storeObj.state.flightOrigin?.split("-")[0] ?? "").trim();

	if (flightPackCode.includes(packCode)) {
		// Only allow recognized airport codes for flight packages
		return airportCodeMap.has(rawOrigin) ? rawOrigin : undefined;
	}

	if (trainPackCode.includes(packCode)) {
		// For trains, allow known station codes or fallback to the first available train station
		if (trainStationCodeMap.has(rawOrigin)) {
			return rawOrigin;
		}
		const firstStation = Array.from(trainStationCodeMap.keys())[0];
		return firstStation ?? undefined;
	}

	return undefined;
};

const getWiypPrice = async (
	packCode: string,
	prestId: string,
	prestations: any[],
	startDate: string,
	endDate: string,
	familyComposition: string,
	site: string,
	acceptLanguage: string,
	storeObj: { state: IStoreState; [key: string]: any },
): Promise<void> => {

	const groupedArray = prepareWiypQuery(storeObj, packCode, prestations, prestId);

	const promises = groupedArray.map(wiypRequest => {
		const skipass = wiypRequest.skipass; // Get skipass duration from the first prestation

		// if (wiypRequest.packCode === "HS") {
		// 	throw new Error("No wiyp request for HS package");
		// }

		if (startDate == "Invalid Date") {
			console.error(`startDate is invalid! in page: ${window.location.href}, for liheId: ${wiypRequest.liheId}, prestations: ${wiypRequest.accommodationIds}, packageid: ${wiypRequest.packageId}`)
		}

		if (endDate == "Invalid Date") {
			console.error(`endDate is invalid! in page: ${window.location.href}, for liheId: ${wiypRequest.liheId}, prestations: ${wiypRequest.accommodationIds}, packageid: ${wiypRequest.packageId}`)
		}

		if (!packCode) {
			console.error(`packCode is missing! in page: ${window.location.href}, for liheId: ${wiypRequest.liheId}, prestations: ${wiypRequest.accommodationIds}, packageid: ${wiypRequest.packageId}`)
		}

		const origin = getOrigin(storeObj, packCode);

		const cacheKey = getCacheKey(
			`${wiypRequest.packCode}-${wiypRequest.packageId}-${origin ?? 'NA'}`,
			startDate,
			endDate,
			storeObj.state.season,
			storeObj.state.participants,
			skipass,
			origin
		);

		// CACHE IS NOT USED ANYMORE - TOO MUCH ATOMICITY ISSUES
		// const cacheResult = attemptToUseCache(cacheKey) as WiypResponse;

		// if (cacheResult) {
		// 	const requestParams = {
		// 		packCode: wiypRequest.packCode,
		// 		startDate,
		// 		endDate,
		// 		origin,
		// 		participants: storeObj.state.participants
		// 	};
		// 	updateStoreWithPrices(cacheResult, storeObj, wiypRequest.packageId, wiypRequest.packCode, requestParams);
		// 	return null; // Return null if using cached result
		// }

		return {
			promise: fetchPrices(
				wiypRequest.packageId,
				wiypRequest.packCode,
				wiypRequest.liheId,
				wiypRequest.accommodationIds,
				startDate,
				endDate,
				familyComposition,
				site,
				acceptLanguage,
				origin
			),
			cacheKey,
			packageId: wiypRequest.packageId,
			requestParams: {
				packCode: wiypRequest.packCode,
				startDate,
				endDate,
				origin,
				participants: storeObj.state.participants
			}
		};
	});
	

	// Filter out null promises (cached results) and wait for remaining WIYP requests to complete
	const validPromises = promises.filter((p): p is NonNullable<typeof p> => p !== null)

	try {
		if (validPromises.length === 0) return

		const wiypPricesArray = await Promise.all(
			validPromises.map((p) =>
				p.promise.catch((error) => {
					console.error("Failed to fetch WIYP prices:", error)
					return {
						packageId: p.packageId,
						origin: undefined,
						response: null,
					}
				})
			)
		)

		wiypPricesArray.forEach(async (wiypPrice, index) => {
			if (wiypPrice.response && wiypPrice.response.wiypPrices && wiypPrice.response.wiypPrices.length > 0) {
			updateMaxPromo(wiypPrice.response, packCode, storeObj)
			const { cacheKey, packageId, requestParams } = validPromises[index]
			updateStoreWithPrices(wiypPrice.response, storeObj, packageId, packCode, requestParams)
			updateResponseCache(cacheKey, wiypPrice.response)
			clearLoadingSet(cacheKey)
			// Store the response in wiypCall
			if (storeObj.dispatch) {
				let storeOrigin = 'NA';
				// Map to LON if present in airportCodeMap and has parent "LON"
				if (wiypPrice.response.flightOrigin) {
					if (
						airportCodeMap.has(wiypPrice.response.flightOrigin) && 
						airportCodeMap.get(wiypPrice.response.flightOrigin)?.parent === "LON"
					) {
						storeOrigin = "LON";
					} else if (airportCodeMap.has(wiypPrice.response.flightOrigin)) {
						storeOrigin = wiypPrice.response.flightOrigin;
					} else {
						storeOrigin = 'NA';
					}
				}

				await storeObj.dispatch("setWiypCall", {
					familyComposition: familyComposition,
					origin: wiypPrice.origin ?? storeOrigin,
					packageCode: packCode,
					packageId: packageId,
					response: wiypPrice.response
				})
				if (wiypPrice.response.flightOrigin && storeObj.state.wiypDefaultFlightOrigin === null) {
					storeObj.commit("setWiypDefaultFlightOrigin", wiypPrice.response.flightOrigin)
					if (trainPackCode.includes(storeObj.state.currentPackCode)) {
						storeObj.commit("setFlightOrigin", wiypPrice.response.flightOrigin)
					}
				}
			}
		} // No WIYP answer for this packageId: remove its prestations from the store
		else {
			const { packageId, requestParams } = validPromises[index]
			removePrestationsWithoutWiypAnswer(storeObj, packCode, packageId, requestParams)
		}
		})
	} finally {
		storeObj.state.calledPrice = true
		window.dispatchEvent(new Event("refreshDataStoredOnWindow"))
	}
}


/**
 * Prepares the WIYP query for the given packCode and prestations
 * @param storeObj the store object
 * @param packCode the packCode
 * @param prestations the prestations
 * @param prestId the prestId
 * @returns the grouped array
 */
function prepareWiypQuery(
	storeObj: { state: IStoreState; [key: string]: any }, 
	requestedPackCode: string, 
	prestations: any[], 
	prestId: string
): {
	current: boolean, 
	packCode: string, 
	liheId: string, 
	packageId: string, 
	accommodationIds: string, 
	skipass: number | undefined 
}[] {
	// Group prestations by fusionId (if present) else liheId, and by prestId
	const aggregationMap: Record<string, {
		current: boolean, 
		packCode: string, 
		liheId: string, 
		packageId: string, 
		accommodationIds: Set<string>, 
		skipass: number | undefined 
	}> = {};

	// Cuurrent packCode is the same as the packCode we are requesting
	if (storeObj.state.packCode === requestedPackCode) {
		prestations.forEach(prest => {
			if ( prest.codePackage === requestedPackCode && (prest.prestId === prestId || prest.codePackage === 'HS')) {
				const finalLiheId = prest.fusionId ?? prest.liheId;
				const key = `${finalLiheId}-${prest.codePackage==='HS' ? `${finalLiheId}HS` : prest.prestId}`;
				if (!aggregationMap[key]) {
					aggregationMap[key] = {
						current: true,
						packCode: prest.codePackage,
						liheId: finalLiheId,
						packageId: prest.codePackage==='HS' ? `${finalLiheId}HS` : prest.prestId,
						accommodationIds: new Set<string>(),
						skipass: prest.skipass,
					};
				}
				aggregationMap[key].accommodationIds.add(prest.prestIdSource);
			}
		});

		// Drop entries whose (liheId, packCode, packageId, accommodationIds, skipass) signature
		// already appeared, so we don't issue duplicate WIYP calls for the same payload.
		const dedupSeen = new Set<string>();
		for (const mapKey of Object.keys(aggregationMap)) {
			const entry = aggregationMap[mapKey];
			const accommodationIdsKey = Array.from(entry.accommodationIds).sort().join('-');
			const dedupKey = `${entry.liheId}-${entry.packCode}-${entry.packageId}-${accommodationIdsKey}-${entry.skipass}`;
			if (dedupSeen.has(dedupKey)) {
				delete aggregationMap[mapKey];
			} else {
				dedupSeen.add(dedupKey);
			}
		}
	}
	// Current packCode is different from the packCode we are requesting
	else {
		storeObj.state.availablePackageCodeWiyp.forEach(prest => {
			if (requestedPackCode === prest.codePackage) {
				const key = `${prest.liheId}-${prest.prestId}`;
				if (!aggregationMap[key]) {
					aggregationMap[key] = {
						current: false,
						liheId: prest.liheId,
						packCode: prest.codePackage,
						packageId: prest.prestId,
						accommodationIds: new Set<string>(),
						skipass: prest.skipass ?? undefined,
					};
				}
				const accommodationIds = new Set<string>();
				prestations.forEach(prest => {
					if (storeObj.state.packCode === prest.codePackage && !prest.fusionId && prest.prestIdSource === prest.prestIdFusion) {
						accommodationIds.add(prest.prestIdSource);
					}
					else if (storeObj.state.packCode === prest.codePackage && prest.fusionId) {
						accommodationIds.add(prest.prestIdFusion);
					}
					
				});
				aggregationMap[key].accommodationIds = accommodationIds;
			}
		});	
	}

	const seen = new Set<string>();
	const groupedArray = Object.values(aggregationMap)
		.map(obj => ({
			current: obj.current,
			liheId: obj.liheId,
			packCode: obj.packCode,
			packageId: obj.packageId,
			accommodationIds: Array.from(obj.accommodationIds).sort().join('-'),
			skipass: obj.skipass,
		}))
		.filter(item => {
			const key = `${item.liheId}-${item.packCode}-${item.packageId}-${item.accommodationIds}-${item.skipass}`;
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});

	return groupedArray;
}


/**
 * Gets the maximum promo for a package code from a WIYP response
 * @param wiypPrice the WIYP response
 * @param packCode the package code
 * @returns the maximum promo for the package code
 */
function updateMaxPromo(wiypPrice: WiypResponse, packCode: string, storeObj: { state: IStoreState; [key: string]: any }) {
	let maxPromoForCurrentPackageCode = 0;
	const packagePrices = wiypPrice.wiypPrices;
	if (packagePrices && typeof packagePrices === "object") {
		for (const wiypPrestation of packagePrices) {
			if (wiypPrestation.publicPriceWiyp && wiypPrestation.sellingPriceWiyp) {
				const promo = Math.round(( wiypPrestation.publicPriceWiyp - wiypPrestation.sellingPriceWiyp) / wiypPrestation.publicPriceWiyp * 100);
				if (promo > maxPromoForCurrentPackageCode) {
					maxPromoForCurrentPackageCode = promo;
				}
			}
		}
	}
	const availablePackCodeAndPrestation: { codePackage: string, prestId: string, maxPromo: number } | undefined = storeObj.state.availablePackageCodeWiyp.find(p => p.codePackage === packCode)
	if (availablePackCodeAndPrestation) {
		availablePackCodeAndPrestation.maxPromo = maxPromoForCurrentPackageCode;
	}
	return maxPromoForCurrentPackageCode;	
}

function attemptToUseCache (cacheKey: string) {
	return responsesCache.value.getResponse("prices", cacheKey)
}

function updateResponseCache(cacheKey: string, wiypResponse: WiypResponse): void {
    // Set the response in the cache
    responsesCache.value.setResponse("prices", cacheKey, wiypResponse);
    
    // Clone the cache to ensure immutability
    responsesCache.value = _.cloneDeep(responsesCache.value);
}

function clearLoadingSet(cacheKey: string): void {
    // Delete the cache key from the loading set
    pricesLoadingSet.value.delete(cacheKey);
    
    // Clone the loading set to ensure immutability
    pricesLoadingSet.value = _.cloneDeep(pricesLoadingSet.value);
}

async function fetchPrices(
	packageId: string,
	packCode: string,
	liheId: string,
	prestIds: string,
	startDate: string,
	endDate: string,
	familyComposition: string,
	site: string,
	acceptLanguage: string,
	origin?: string
): Promise<{packageId: string, origin?: string, response: WiypResponse}> {
	const originToUse = origin || undefined;
	try {
		const response = await window.wiypService.getWiypPrices({
			packageId,
			packCode,
			liheId,
			prestIds,
			startDate,
			endDate,
			familyComposition,
			site,
			acceptLanguage,
			origin: originToUse
		})

		return {
			packageId,
			origin: originToUse,
			response
		}
	} catch (error) {
		console.error("Failed to fetch prices:", error)
		throw new Error("Failed to fetch prices from service")
	}
}

function updateStoreWithPrices(
	priceResponse: WiypResponse,
	storeObj: { state: IStoreState },
	packageId: string,
	packCode: string,
	requestParams?: {
		packCode: string,
		startDate: string,
		endDate: string,
		origin: string | undefined,
		participants: number[]
	}
)  {
	// Validate that the response matches current state (dates, origin, participants) to prevent stale updates
	// We allow updates for any packCode (since WIYP calls are made for multiple packages),
	// but only if the dates/origin/participants match current state
	if (requestParams) {
		const currentOrigin = storeObj.state.flightOrigin?.split("-")[0] || ''
		const currentStartDate = formatDates(storeObj.state.dates.BeginDate)
		const currentEndDate = formatDates(storeObj.state.dates.EndDate)
		const participantsMatch = JSON.stringify(storeObj.state.participants) === JSON.stringify(requestParams.participants)
		
		// Only update if dates, origin, and participants match current state
		// (packCode can differ since we call WIYP for multiple packages)
		if (
			requestParams.startDate !== currentStartDate ||
			requestParams.endDate !== currentEndDate ||
			requestParams.origin !== currentOrigin ||
			!participantsMatch
		) {
			// This response is for a different state (different dates/origin/participants), skip updating to prevent blinking
			return
		}
	}

	// Only merge prices for the specific packCode instead of replacing entire map
	// This prevents other packages' prices from being reset
	const existingPrices = storeObj.state.prestMaps.prices || {}
	const existingPackPrices = existingPrices[packCode] || {}
	
	// Clone the existing prices for this packCode and merge with availabilities
	const quotePriceMap = _.cloneDeep({
		...storeObj.state.prestMaps.seAvailabilities[packCode],
		...existingPackPrices
	})
	
	// Create a temporary structure for enrichAvailabilitiesWithPrices
	const tempAvailabilities: ISeAvailabilities = {
		[packCode]: quotePriceMap
	}
	
	enrichAvailabilitiesWithPrices(priceResponse, tempAvailabilities, packageId, packCode);
	
	// Only update prices for this specific packCode, preserving other packages
	storeObj.state.prestMaps.prices = {
		...existingPrices,
		[packCode]: quotePriceMap
	}
	
	storeObj.state.calledPrice = true
}

/**
 * Removes all prestations (offers) belonging to a given packageId from the store
 * when the WIYP endpoint returned no price answer for that package.
 * Offers are matched on `prestId` (which equals the WIYP packageId).
 */
function removePrestationsWithoutWiypAnswer(
	storeObj: { state: IStoreState },
	packCode: string,
	packageId: string,
	requestParams?: {
		packCode: string,
		startDate: string,
		endDate: string,
		origin: string | undefined,
		participants: number[]
	}
): void {
	// Guard against stale responses: skip if dates/origin/participants changed since the request
	if (requestParams) {
		const currentOrigin = storeObj.state.flightOrigin?.split("-")[0] || ''
		const currentStartDate = formatDates(storeObj.state.dates.BeginDate)
		const currentEndDate = formatDates(storeObj.state.dates.EndDate)
		const participantsMatch = JSON.stringify(storeObj.state.participants) === JSON.stringify(requestParams.participants)

		if (
			requestParams.startDate !== currentStartDate ||
			requestParams.endDate !== currentEndDate ||
			requestParams.origin !== currentOrigin ||
			!participantsMatch
		) {
			return
		}
	}

	const filterOutPackage = (offers?: { [id: string]: PackageOffer }) =>
		offers
			? Object.fromEntries(
				Object.entries(offers).filter(([, offer]) => offer.prestId !== packageId)
			) as { [id: string]: PackageOffer }
			: offers

	// Reassign top-level maps (consistent with updateStoreWithPrices) to keep reactivity
	const seAvailabilities = storeObj.state.prestMaps.seAvailabilities
	if (seAvailabilities[packCode]) {
		storeObj.state.prestMaps.seAvailabilities = {
			...seAvailabilities,
			[packCode]: filterOutPackage(seAvailabilities[packCode]) as { [id: string]: PackageOffer }
		}
	}

	const prices = storeObj.state.prestMaps.prices
	if (prices[packCode]) {
		storeObj.state.prestMaps.prices = {
			...prices,
			[packCode]: filterOutPackage(prices[packCode]) as { [id: string]: PackageOffer }
		}
	}
}

// Helper function to format dates from "MM/DD/YYYY" to "YYYY-MM-DD"
function formatDates(date: string): string {
	return date.split('/').reverse().join('-');
}

// Function to enrich availabilities with prices
function enrichAvailabilitiesWithPrices(
    priceResponse: WiypResponse,
    availabilities: ISeAvailabilities,
	packageId: string,
	packCode: string
): void {
    const wiypPrestations: WiypPrestation[] = priceResponse.wiypPrices || [];
    const iresaPrestations: IResaPrestation[] = priceResponse.iresaPrices || [];

	const offers: { [id: string]: PackageOffer } = availabilities[packCode];
	Object.values(offers).forEach((offer: PackageOffer) => {
	
		if(wiypPrestations.length <= 0 && iresaPrestations.length <= 0 && locale === 'en_US') {
			// For UK locale (en_US), if WIYP price is not available, hide the price and gray out the btn. This logic is for transport packages only.
			const isFlightOrTransportPackage = (flightPackCode.includes(packCode) || transportPackCode.includes(packCode))
			if (locale === 'en_US' && isFlightOrTransportPackage) {
				clearPricing(offer);
				// Set price to 0 to indicate no price available from WIYP endpoint (will be handled by display logic)
				offer.pricePerLogement = 0;
				return;
			}
		}
		
		const wiypPrestation = wiypPrestations.find(
			(wiyp) => wiyp.prestId === offer.prestIdSource && packageId === offer.prestId
		);

		if (wiypPrestation) {
			applyWiypPricing(offer, wiypPrestation);
			return
		} else {
			const iresaPrestation = iresaPrestations.find(
				(iresa) => iresa.prestId === offer.prestIdSource && packageId === offer.prestId
			);
			if (iresaPrestation) {
				applyIresaPricing(offer, iresaPrestation);
			}
		}
	});
}

function applyWiypPricing(offer: PackageOffer, wiypPrestation: WiypPrestation) {
    offer.pricePerLogement = Math.round(wiypPrestation.sellingPriceWiyp);

	// For test only, wait for Yona to update wiyp
	if (wiypPrestation.accomPriceWiyp) {
		offer.priceAccommodationOnly = Math.round(wiypPrestation.accomPriceWiyp);
	}

    if (wiypPrestation.publicPriceWiyp) {
        offer.crossedPrice = wiypPrestation.publicPriceWiyp;
        // Calculate the promo percentage
        const discount = wiypPrestation.publicPriceWiyp - wiypPrestation.sellingPriceWiyp;
        offer.maxPromo = Math.round((discount / wiypPrestation.publicPriceWiyp) * 100);
    } else {
		// If no Wiyp price is found, clear any existing pricing on the offer
        clearPricing(offer);
    }
}

function applyIresaPricing(offer: PackageOffer, iresaPrestation: IResaPrestation) {
    offer.pricePerLogement = Math.round(iresaPrestation.sellingPriceIresa);
}

function clearPricing(offer: PackageOffer) {
    delete offer.crossedPrice;
    delete offer.maxPromo;
}
export default getWiypPrice
